import { h, type ComponentType, type FunctionComponent } from 'preact';
import { createPreference } from '@brainfocus/web/shared/storage/preference';

/**
 * Whether this tab already reloaded once for a chunk that failed to load (#1169).
 * Per session: a later session starts clean, and a successful load clears it.
 */
export const chunkReloadPreference = createPreference<boolean>('chunk-reload', {
    version: 1,
    defaultValue: () => false,
    parse: raw => (typeof raw === 'boolean' ? raw : undefined),
    storage: 'session',
});

export interface LazyComponent<P> extends FunctionComponent<P> {
    /** Start loading the module now (a route's loader, ahead of render); resolves once it is loaded. */
    preload(): Promise<void>;
}

export interface LazyComponentOptions {
    /** How the page reloads to fetch the current build. */
    reload?: () => void;
}

/**
 * A component whose code loads on demand (#1169): a route page, or the
 * neuroimaging viewers and vtk.js. Render it inside a `Suspense` (from
 * `preact/compat`): until the module has loaded it suspends; once it has —
 * `preload()` from a route's loader, or an earlier render — it renders at once,
 * with no fallback. The module is requested once, whoever asks first.
 *
 * A tab opened before a deploy asks for chunk files that no longer exist. The
 * first failed load in a session reloads the page to fetch the current build; a
 * second one is rethrown, so a genuinely broken build never reload-loops.
 */
export function lazyComponent<P>(load: () => Promise<ComponentType<P>>, options: LazyComponentOptions = {}): LazyComponent<P> {
    const reload = options.reload ?? (() => window.location.reload());
    let loaded: ComponentType<P> | null = null;
    let failure: { error: unknown } | null = null;
    let loading: Promise<void> | null = null;

    const loadOnce = (): Promise<void> => {
        loading ??= load().then(
            (component) => {
                loaded = component;
                if (chunkReloadPreference.get()) chunkReloadPreference.set(false);
            },
            (error: unknown) => {
                if (chunkReloadPreference.get()) {
                    failure = { error };
                    throw error;
                }
                chunkReloadPreference.set(true);
                reload();
                // The page is going away: never settle, so nothing renders a broken state meanwhile.
                return new Promise<void>(() => {});
            },
        );
        return loading;
    };

    const Lazy = ((props: P) => {
        if (loaded) return h(loaded as ComponentType<object>, props as object);
        if (failure) throw failure.error;
        // The Suspense protocol: a thrown promise suspends until it settles, then renders again.
        throw loadOnce().catch(() => undefined);
    }) as LazyComponent<P>;
    Lazy.preload = loadOnce;
    return Lazy;
}
